import React, { useState, useEffect, useMemo } from "react";
import { useFetchInterviews } from "../../hooks/useFetchInterviews";
import { useAuth } from "../../contexts/AuthContext";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import { Button } from "../../components/ui/Button";
import { Skeleton } from "../../components/ui/Skeleton";
import { httpsCallable } from "firebase/functions";
import { functions } from "src/api/firebase";
import { formatISODateString } from "src/utils/timeUtils";
import { AssessmentCard } from "./AssessmentCard";
import NoReportDialog from "./NoReportDialog";
import { AssessmentRecord } from "./types";
import { useFetchCases } from "../../hooks/useFetchCases";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/Select";
import { ScrollArea } from "../../components/ui/ScrollArea";
import { Checkbox } from "../../components/ui/CheckBox";

const PAGE_SIZE = 8;

const AssessmentHistoryPage: React.FC = () => {
    const {
        interviews,
        isLoading: interviewsLoading,
        error: interviewsError,
        refetch,
    } = useFetchInterviews();
    const { firebaseUser } = useAuth();
    const navigate = usePartnerAwareNavigate();
    console.log("Fetched interviews", interviews);
    // Get unique caseProblemIds from interviews
    const caseProblemIds = useMemo(() => {
        const ids = interviews
            .map((interview) => interview.caseProblemId)
            .filter((id): id is number => id !== undefined);
        return Array.from(new Set(ids));
    }, [interviews]);
    // Fetch cases data
    const { data: cases } = useFetchCases(caseProblemIds);

    // Create a map of caseProblemId to case title
    const caseTitleMap = useMemo(() => {
        return new Map(cases.map((case_) => [case_.id, case_.title]));
    }, [cases]);

    // State for reports
    const [reports, setReports] = useState<any[]>([]);
    const [reportsLoading, setReportsLoading] = useState(true);
    const [noReportDialogOpen, setNoReportDialogOpen] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [selectedTitle, setSelectedTitle] = useState("");
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);

    // Combined state for filtering
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<string>("All");
    const [searchTerm, setSearchTerm] = useState<string>("");

    // Add new filter options
    const FILTER_OPTIONS = {
        status: ["All", "Completed", "Attempted"],
    };

    // Shared Option Component
    const Option = ({
        label,
        value,
        isSelected,
        isMulti,
        onClick,
    }: {
        label: string;
        value: string;
        isSelected: boolean;
        isMulti: boolean;
        onClick: () => void;
    }) => (
        <div
            className={`flex items-center gap-2 px-4 py-2 rounded cursor-pointer hover:bg-[var(--color-primary-hover)]  ${
                isSelected ? "bg-[#F1F2F3]" : ""
            }`}
            onClick={onClick}>
            {isMulti && (
                <Checkbox
                    checked={isSelected}
                    className="flex-shrink-0 w-4 h-4 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
            )}
            <span>{label}</span>
        </div>
    );

    // Handle single select change
    const handleSingleSelect = (field: string, value: string) => {
        if (field === "status") {
            setStatusFilter(value);
        }
    };

    // Helper functions
    const formatDate = (timestamp: any) => {
        if (!timestamp) return "N/A";
        let seconds = timestamp.seconds || timestamp._seconds;
        if (!seconds) return "N/A";

        try {
            const date = new Date(seconds * 1000);
            return date.toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            });
        } catch (e) {
            console.error("Date formatting error:", e);
            return "Invalid date";
        }
    };

    const formatDuration = (
        startTime?: any,
        endTime?: any,
        isActive?: boolean
    ) => {
        if (!startTime || !endTime) {
            if (isActive === true) {
                return "Active";
            }
            return startTime ? "Not finished" : "Not started";
        }

        const start = startTime.seconds || startTime._seconds;
        const end = endTime.seconds || endTime._seconds;
        if (!start || !end) return "Not available";

        const durationSeconds = end - start;
        if (durationSeconds < 0) return "Not available";
        if (durationSeconds < 10) return "Too short";

        const minutes = Math.floor(durationSeconds / 60);
        const seconds = durationSeconds % 60;
        return `${minutes}m ${seconds}s`;
    };

    const getStatusText = (status?: string, isActive?: boolean) => {
        if (isActive === false) return "Completed";
        if (isActive === true) return "Attempted";
        return status || "Unknown";
    };

    // Fetch reports
    useEffect(() => {
        const fetchReports = async () => {
            if (firebaseUser) {
                try {
                    const getReports = httpsCallable(functions, "getReports");
                    const response = await getReports({
                        uid: firebaseUser?.uid,
                    });
                    const data = response.data as {
                        success: boolean;
                        data: any;
                        error?: string;
                    };
                    if (data.success) {
                        setReports(data.data);
                    }
                } catch (error) {
                    console.error("Error fetching reports:", error);
                } finally {
                    setReportsLoading(false);
                }
            }
        };

        fetchReports();
    }, [firebaseUser]);

    // Memoize the base records
    const baseRecords = useMemo(() => {
        // Create a map of interview IDs that have reports
        const reportMap = new Map(reports.map((report) => [report.id, report]));

        const records: AssessmentRecord[] = interviews.map((interview) => {
            const report = reportMap.get(interview.id);

            // If there's a report for this interview, use the report data
            if (report) {
                return {
                    id: report.id,
                    title: report.data.metadata.title,
                    date: formatISODateString(report.data.metadata.date),
                    duration:
                        report.data.metadata.time?.minutes > 0
                            ? `${report.data.metadata.time.minutes} mins`
                            : "Not available",
                    status: "Completed",
                    type: "report",
                    score: report.data.performance_summary?.overallPerformance
                        ?.score,
                    reportStatus: report.data.reportStatus,
                    hasReport: true,
                };
            }

            // Otherwise use the interview data
            return {
                id: interview.id,
                title: interview.caseProblemId
                    ? caseTitleMap.get(interview.caseProblemId) ||
                      `Case #${interview.caseProblemId}`
                    : "Untitled Interview",
                date: formatDate(interview.startTime),
                duration: formatDuration(
                    interview.startTime,
                    interview.endTime,
                    interview.isActive
                ),
                status: getStatusText(interview.status, interview.isActive),
                reportStatus: interview.reportStatus,
                type: "interview",
                isActive: interview.isActive,
                hasReport: reportMap.has(interview.id),
            };
        });
        console.log("Base records", records);
        // Add any reports that don't have corresponding interviews
        // (this shouldn't happen in normal operation, but handles edge cases)
        reports.forEach((report) => {
            if (!interviews.some((interview) => interview.id === report.id)) {
                records.push({
                    id: report.id,
                    title: report.data.metadata.title,
                    date: formatISODateString(report.data.metadata.date),
                    duration: `${report.data.metadata.time.minutes} mins`,
                    status: "Completed",
                    type: "report",
                    score: report.data.performance_summary?.overallPerformance
                        ?.score,
                    reportStatus: report.data.reportStatus,
                    hasReport: true,
                });
            }
        });
        console.log("Base records 22", records);
        return records;
    }, [interviews, reports, caseTitleMap]);

    // Apply filters using memoization
    const filteredRecords = useMemo(() => {
        let filtered = [...baseRecords];

        if (statusFilter !== "All") {
            filtered = filtered.filter((record) => {
                if (statusFilter === "Completed") {
                    return (
                        record.status === "Completed" ||
                        record.isActive === false
                    );
                } else if (statusFilter === "Attempted") {
                    return (
                        record.status === "Attempted" ||
                        record.isActive === true
                    );
                }
                return record.status === statusFilter;
            });
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (record) =>
                    record.title.toLowerCase().includes(term) ||
                    record.id.toLowerCase().includes(term)
            );
        }
        console.log("Filtered records", filtered);
        return filtered;
    }, [baseRecords, statusFilter, searchTerm]);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredRecords.length]);

    const handleRecordClick = (record: AssessmentRecord) => {
        if (record.type === "report") {
            navigate(`/report?interviewSessionId=${record.id}`);
        } else if (record.hasReport) {
            navigate(`/report?interviewSessionId=${record.id}`);
        } else if (record.isActive === false) {
            // Interview is completed but has no report
            setSelectedTitle(record.title);
            setErrorDialogOpen(true);
        } else {
            // Interview is not completed
            setSelectedTitle(record.title);
            setNoReportDialogOpen(true);
        }
    };

    const handleGenerateReport = async (record: AssessmentRecord) => {
        try {
            setIsGeneratingReport(true);
            const generateReport = httpsCallable(
                functions,
                "initiateReportGeneration"
            );
            const response = await generateReport({
                uid: firebaseUser?.uid,
                sessionId: record.id,
            });
            const data = response.data as { success: boolean; error?: string };

            if (data.success) {
                // Refetch reports to show the newly generated one
                await refetch();
                // Show success message
                alert("Report generated successfully!");
            } else {
                throw new Error(data.error || "Failed to generate report");
            }
        } catch (error) {
            console.error("Error generating report:", error);
            alert("Failed to generate report. Please try again later.");
        } finally {
            setIsGeneratingReport(false);
        }
    };

    if (!firebaseUser) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">Please log in</h2>
                    <Button onClick={() => navigate("/login")}>Login</Button>
                </div>
            </div>
        );
    }

    const isLoading = interviewsLoading || reportsLoading;
    const paginatedRecords = filteredRecords.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE
    );
    console.log("Paginated records", paginatedRecords);
    const totalPages = Math.ceil(filteredRecords.length / PAGE_SIZE);

    return (
        <>
            <div className="container mx-auto p-4">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Assessment History</h1>
                </div>

                <div className="flex flex-col w-full mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Search Filter */}
                        <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium">
                                Search
                            </label>
                            <input
                                type="text"
                                placeholder="Search by title"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="flex flex-col">
                            <label className="mb-1 text-sm font-medium">
                                Status
                            </label>
                            <Select
                                value={statusFilter}
                                onValueChange={(value) =>
                                    handleSingleSelect("status", value)
                                }>
                                <SelectTrigger className="w-full">
                                    <SelectValue>{statusFilter}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <ScrollArea className="h-[200px]">
                                        {FILTER_OPTIONS.status.map((option) => (
                                            <Option
                                                key={option}
                                                label={option}
                                                value={option}
                                                isSelected={
                                                    statusFilter === option
                                                }
                                                isMulti={false}
                                                onClick={() =>
                                                    handleSingleSelect(
                                                        "status",
                                                        option
                                                    )
                                                }
                                            />
                                        ))}
                                    </ScrollArea>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                        <p className="text-gray-600">
                            Loading your assessments...
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 w-full">
                            {[...Array(6)].map((_, i) => (
                                <Skeleton key={i} className="h-48" />
                            ))}
                        </div>
                    </div>
                ) : filteredRecords.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="bg-gray-100 rounded-full p-6 mb-4">
                            <svg
                                className="w-12 h-12 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            No assessments found
                        </h3>
                        <p className="text-gray-600 text-center max-w-md">
                            {searchTerm || statusFilter !== "All"
                                ? "Try adjusting your filters or search terms"
                                : "Start your first assessment to see your history here"}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {paginatedRecords.map((record) => (
                                <AssessmentCard
                                    key={`${record.type}-${record.id}`}
                                    record={record}
                                    onClick={handleRecordClick}
                                    onGenerateReport={handleGenerateReport}
                                />
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="flex justify-center mt-6 gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        setCurrentPage((p) =>
                                            Math.max(1, p - 1)
                                        )
                                    }
                                    disabled={currentPage === 1}>
                                    Previous
                                </Button>
                                <span className="mx-4 flex items-center">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        setCurrentPage((p) =>
                                            Math.min(totalPages, p + 1)
                                        )
                                    }
                                    disabled={currentPage === totalPages}>
                                    Next
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <NoReportDialog
                isOpen={noReportDialogOpen}
                onClose={() => setNoReportDialogOpen(false)}
                title={selectedTitle}
            />

            {/* New Error Dialog for completed interviews without reports */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
                    errorDialogOpen ? "block" : "hidden"
                }`}>
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                    <h2 className="text-xl font-semibold mb-4">
                        Report Not Available
                    </h2>
                    <p className="text-gray-600 mb-4">
                        We apologize, but we couldn't find the report for your
                        completed interview "{selectedTitle}". This might be due
                        to a technical issue.
                    </p>
                    <p className="text-gray-600 mb-6">
                        Please contact WeKruit support at{" "}
                        <a
                            href="mailto:admin1@wekruit.com"
                            className="text-primary hover:underline">
                            admin1@wekruit.com
                        </a>{" "}
                        for assistance.
                    </p>
                    <div className="flex justify-end">
                        <Button onClick={() => setErrorDialogOpen(false)}>
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AssessmentHistoryPage;
